import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { InputPassword } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { PASSWORD_PATTERN } from 'src/utils/validation/validation-patterns';

import PasswordRequirements from '../PasswordRequirements/PasswordRequirements';

const setPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        PASSWORD_PATTERN,
        'Password must contain at least one lowercase letter, one uppercase letter and a special character',
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type ISetPasswordForm = z.infer<typeof setPasswordSchema>;

type Props = {
  onSubmit: (data: Omit<ISetPasswordForm, 'confirmPassword'>) => Promise<void>;
};

const SetPasswordForm = ({ onSubmit }: Props) => {
  const form = useForm<ISetPasswordForm>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = useWatch({ control: form.control, name: 'password' });
  const passwordError = form.formState.errors.password;

  const handleSubmit = async (data: ISetPasswordForm) => {
    const { confirmPassword, ...submitData } = data;
    await onSubmit(submitData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New password</FormLabel>
              <FormControl>
                <InputPassword {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PasswordRequirements password={password} hasError={Boolean(passwordError)} />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm password</FormLabel>
              <FormControl>
                <InputPassword {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          size="lg"
          className="w-full mt-4"
          isLoading={form.formState.isSubmitting}
          disabled={form.formState.isSubmitting}
        >
          Create password
        </Button>
      </form>
    </Form>
  );
};

export default SetPasswordForm;
